import { Component, Input, OnInit, ChangeDetectionStrategy } from "@angular/core";

import { BaseComponent, ProductService, StorageKeys, StorageService, UserService, WindowService } from "@mypxplat/xplat/core";
import { Router, ActivatedRoute } from "@angular/router";
import { TranslateService } from "@ngx-translate/core";
import { AppService, WebCommunityService } from "@mypxplat/xplat/web/core";
import { environment } from "@mypxplat/xplat/core";
import { RegisterProductComponent } from "../../../modals/register-product/register-product.component";

/**
 * Account → Connections. Phase 0 hosts a single "Connect with Discord" integration: the member can
 * link a Discord account (OAuth via paeapi) and unlink it. Designed to grow to further providers.
 *
 * Embeddable: pass `[compact]="true"` to hide the "Connections" heading + intro copy so a parent
 * (e.g. the community onboarding wizard) can supply its own contextual messaging. The Discord row
 * itself, gift-redemption UI, and success/error banners stay put.
 */
@Component({
  changeDetection: ChangeDetectionStrategy.Eager,
  selector: "myp-account-connections",
  templateUrl: "./connections.component.html",
  styleUrls: ["./connections.component.scss"],
  standalone: false,
})
export class ConnectionsComponent extends BaseComponent implements OnInit {
  /**
   * When true, skip the internal "Connections" heading + intro paragraph. Used by the community
   * onboarding wizard, which surrounds this component with its own community-specific copy.
   */
  @Input() compact = false;
  /**
   * Where to send the member after the Discord round trip, when this panel is NOT on the
   * account page — the OAuth callback always returns to /account (it has no idea where the
   * flow began), so the path is stashed before leaving and consumed on the way back.
   */
  @Input() returnTo = "";
  public loading = true;
  public working = false;
  public redeeming = false;
  public linked = false;
  public discordLink: any = null;
  public giftRedeemed = false;
  public giftOpen = true; // offer window; backend is the source of truth (DISCORD_FSP_END_DATE)
  public ownsGiftProduct = false; // already owns Fender Studio Pro (fallback product — one per account)
  public requiresToneMasterPro = false; // backend feature flag; when off, hardware is not checked
  public hasToneMasterPro = true; // irrelevant unless requiresToneMasterPro is true
  public message: string = "";
  public messageType: "success" | "error" | "" = "";

  /**
   * Eligible to claim: linked, guild-verified, offer open, not already redeemed, and — when the
   * backend requires it — a registered Tone Master Pro.
   *
   * Owning Fender Studio Pro is deliberately NOT disqualifying. Existing owners receive the
   * upgrade SKU as a support-date extension instead of a second license, so they must be able to
   * click through; excluding them here would leave that backend path unreachable.
   */
  get canRedeem(): boolean {
    return this.linked && !!this.discordLink?.verified && !this.giftRedeemed && this.giftOpen && this.meetsHardwareRequirement;
  }

  /** Hardware gate: satisfied when the backend isn't enforcing it, or when the member owns a TMP. */
  get meetsHardwareRequirement(): boolean {
    return !this.requiresToneMasterPro || this.hasToneMasterPro;
  }

  /** Eligible in every respect except the Tone Master Pro requirement — prompt them to register it. */
  get needsToneMasterPro(): boolean {
    return this.linked && !!this.discordLink?.verified && !this.giftRedeemed && this.giftOpen && !this.meetsHardwareRequirement;
  }

  /** Linked but not a verified guild member — must join the server (and re-link) to become eligible. */
  get needsGuildMembership(): boolean {
    return this.linked && !this.discordLink?.verified && !this.giftRedeemed && this.giftOpen;
  }

  constructor(
    userService: UserService,
    public appService: AppService,
    public communityService: WebCommunityService,
    private _win: WindowService,
    private _router: Router,
    private _route: ActivatedRoute,
    private _translate: TranslateService,
    private _storage: StorageService,
    private _productService: ProductService
  ) {
    super(userService);
  }

  ngOnInit() {
    this.handleReturnParams();
    this.refreshStatus();
  }

  /** Thin wrapper so the status/error strings below stay readable at their call sites. */
  private t(key: string): string {
    return this._translate.instant(key);
  }

  /** Read the `?discord=success|error(&reason=...)` the callback appended, show a note, clean the URL. */
  private handleReturnParams() {
    const params = this._route.snapshot.queryParams;
    const outcome = params["discord"];
    if (!outcome) return;

    // The flow started somewhere else (the TMP migration modal, say). Hand the result back to
    // that page rather than reporting it here — it is the page with the context. Consumed
    // before navigating so a bounce can't loop, and only when we aren't already there.
    const returnTo = this._storage.getItem(StorageKeys.DISCORD_LINK_RETURN_TO);
    if (returnTo) {
      this._storage.removeItem(StorageKeys.DISCORD_LINK_RETURN_TO);
      // Must be a relative in-app path. Anything else is a junk value rather than a destination
      // this app wrote, and handing it to the router only produces a nonsense navigation.
      const isInAppPath = returnTo.startsWith("/") && !returnTo.startsWith("//");
      if (isInAppPath && returnTo.split("?")[0] !== window.location.pathname) {
        const [path, search] = returnTo.split("?");
        const carried: Record<string, any> = { ...this._parseSearch(search), discord: outcome, reason: params["reason"] || null };
        // A separate marker from `discord=success` because the two are consumed by different
        // things: `discord` is this panel's own banner, while `discordLinkSuccess` tells the
        // page that started the flow to move the member on to what they can now claim. Without
        // it the community page just re-opens the "link your Discord account" welcome, which
        // reads as though the link failed.
        if (outcome === "success") carried["discordLinkSuccess"] = "true";
        this._router.navigate([path], { queryParams: carried, replaceUrl: true });
        return;
      }
    }
    if (outcome === "success") {
      this.message = this.t("discord.link_success");
      this.messageType = "success";
    } else {
      this.message = this.errorMessageFor(params["reason"]);
      this.messageType = "error";
    }
    this._win.setTimeout(() => {
      this._router.navigate([], { queryParams: { discord: null, reason: null }, queryParamsHandling: "merge" });
    }, 0);
  }

  /** Query string of a stashed return path, as an Angular queryParams object. */
  private _parseSearch(search: string): Record<string, string> {
    const out: Record<string, string> = {};
    if (!search) return out;
    new URLSearchParams(search).forEach((value, key) => (out[key] = value));
    return out;
  }

  private errorMessageFor(reason: string): string {
    switch (reason) {
      case "in_use":
        return this.t("discord.error_in_use");
      case "already_linked":
        return this.t("discord.error_already_linked");
      case "state":
        return this.t("discord.error_state");
      default:
        return this.t("discord.error_generic");
    }
  }

  private refreshStatus() {
    this.loading = true;
    this.communityService.getDiscordLink().subscribe({
      next: (res: any) => {
        this.linked = !!res?.linked;
        this.discordLink = res?.link || null;
        this.giftRedeemed = !!res?.giftRedeemed;
        this.giftOpen = res?.giftOpen !== false; // default open unless the backend says closed
        this.ownsGiftProduct = !!res?.ownsGiftProduct;
        this.requiresToneMasterPro = !!res?.requiresToneMasterPro;
        // Default true so a backend that isn't sending the field yet never blocks anyone.
        this.hasToneMasterPro = res?.hasToneMasterPro !== false;
        this.loading = false;
      },
      error: () => {
        this.loading = false;
      },
    });
  }

  /** Start the OAuth flow: fetch the authorize URL (authenticated XHR), then navigate the browser to Discord. */
  connect() {
    this.working = true;
    // Whatever a previous attempt stashed is dead: THIS attempt decides where the round trip
    // comes back to. Without clearing, an attempt abandoned at Discord's consent screen leaves
    // its destination behind, and the next link started somewhere with no `returnTo` — the
    // account page — gets redirected to wherever that abandoned flow began.
    this._storage.removeItem(StorageKeys.DISCORD_LINK_RETURN_TO);
    this.communityService.getDiscordAuthorizeUrl().subscribe({
      next: (res: any) => {
        if (res?.url) {
          // Stashed here rather than above so it is written only once we know we are actually
          // leaving. The error paths below keep the member on this page, where a stash would
          // be just as stale as an abandoned one.
          if (this.returnTo) this._storage.setItem(StorageKeys.DISCORD_LINK_RETURN_TO, this.returnTo);
          window.location.href = res.url;
        } else {
          this.working = false;
          this.message = this.t("discord.error_start");
          this.messageType = "error";
        }
      },
      error: () => {
        this.working = false;
        this.message = this.t("discord.error_start");
        this.messageType = "error";
      },
    });
  }

  /** Claim the one-time Fender Studio Pro gift. The backend re-verifies eligibility. */
  redeem() {
    this.redeeming = true;
    this.communityService.redeemDiscordGift().subscribe({
      next: (res: any) => {
        this.redeeming = false;
        // The response carries the authoritative state, so adopt it rather than deriving it.
        // `redeemedAt` being present is the whole signal — the client deliberately does not
        // distinguish a new license from a support extension, since the member gets Fender
        // Studio Pro either way. That split exists only for reporting.
        this.giftRedeemed = !!res?.giftRedemption?.redeemedAt || !!res?.giftRedeemed;
        this.ownsGiftProduct = !!res?.ownsGiftProduct;
        this.message = this.t(res?.alreadyRedeemed ? "discord.gift_already_redeemed" : "discord.gift_success");
        this.messageType = "success";
        // Refresh before navigating: the product detail page resolves from the cached product
        // list, so without this it cannot find the license just granted.
        this._productService.getProducts(true).subscribe({ error: () => {} });
        // Send them to the product they just received.
        this._router.navigate(["/products/detail/", environment.product_ids.fsp8pro]);
      },
      error: (err: any) => {
        this.redeeming = false;
        const reason = err?.error?.reason;
        if (reason === "not_verified") {
          this.message = this.t("discord.gift_not_verified");
        } else if (reason === "not_linked") {
          this.message = this.t("discord.gift_not_linked");
        } else if (reason === "offer_ended") {
          this.giftOpen = false;
          this.message = this.t("discord.gift_offer_ended");
        } else if (reason === "needs_tone_master_pro") {
          // Flip the local flag so the register prompt replaces the redeem button immediately,
          // without waiting for a refresh.
          this.hasToneMasterPro = false;
          this.requiresToneMasterPro = true;
          this.message = this.t("discord.gift_needs_tone_master_pro");
        } else {
          this.message = this.t("discord.gift_error_generic");
        }
        this.messageType = "error";
      },
    });
  }

  /**
   * Open the register-product modal so the member can add their Tone Master Pro, then re-read
   * status on close so the redeem button appears without a page reload. Same call shape as
   * `<myp-create-post-input>`, which opens it for the same reason — to let an ownership gate
   * re-evaluate afterwards.
   *
   * A serial that cannot be validated will not produce an asset, so the member stays gated; the
   * modal routes those cases to the missing-serial flow and support, which is intended.
   */
  registerToneMasterPro() {
    const ref = this.appService.showModal(RegisterProductComponent, {
      size: "lg",
      ariaLabelledBy: "modal-title",
      container: this.appService.appContainer,
    });
    const done = () => this.refreshStatus();
    ref?.result?.then(done, done);
  }

  unlink() {
    this.working = true;
    this.communityService.unlinkDiscord().subscribe({
      next: () => {
        this.working = false;
        this.linked = false;
        this.discordLink = null;
        this.message = this.t("discord.unlink_success");
        this.messageType = "success";
      },
      error: () => {
        this.working = false;
        this.message = this.t("discord.unlink_error");
        this.messageType = "error";
      },
    });
  }
}
